import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import WidgetFrame from "./WidgetFrame";
import EmptyRow from "./EmptyRow";
import OperationsAssetMap from "./OperationsAssetMap";
import { Skeleton } from "../Skeleton";
import Select from "../ReusableComponents/Select";
import {
  applyVisibleOrder,
  assetGroupOptions,
  assetReading,
  filterAssetCards,
  orderAssetCards,
  readAssetOrder,
  writeAssetOrder,
  MODEL_TEMPLATES_PATH,
  type AssetCard,
  type AssetReading,
  type GenerationComparison,
  type SignalSeverity,
} from "../../utils/operationsDashboard";

interface AssetMapWidgetProps {
  cards: AssetCard[];
  /** Comparisons by composed-forecast id — what gives a card its live generation reading. */
  comparisons?: Map<string, GenerationComparison | null>;
  isLoading?: boolean;
  className?: string;
}

// The card's ground says what the asset is doing before a word is read: red failing, pale accent watching, plain clean
const CARD_TONE: Record<SignalSeverity, string> = {
  negative: "border-eai-danger-border bg-eai-danger-soft",
  attention: "border-eai-accent-lt bg-eai-checklist-lt",
  positive: "border-eai-success-border bg-eai-success-surface-soft",
  neutral: "border-eai-border bg-eai-surface-card",
};

// The map takes what the fixed card strip leaves; the floor is for the stacked layout, which has no widget height to divide
const MAP_CLASS = "mx-2.5 min-h-64 flex-1 lg:min-h-0";

// The order key for the no-group view, which has no id of its own to store an order under
const ALL_GROUPS_ORDER_KEY = "__all";

// Zero reads as nothing here rather than as "0 models" — the status line above already said that
const countOf = (count: number, noun: string) =>
  count ? `${count} ${noun}${count === 1 ? "" : "s"}` : null;

interface AssetTileProps {
  card: AssetCard;
  /** The asset's live generation number, or null when nothing it links to has one. */
  reading: AssetReading | null;
  isSelected: boolean;
  onSelect: (id: string) => void;
  registerNode: (id: string, node: HTMLElement | null) => void;
}

const AssetTile: React.FC<AssetTileProps> = ({
  card,
  reading,
  isSelected,
  onSelect,
  registerNode,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });
  const bad = card.severity === "negative";
  const counts = [
    countOf(card.modelLabels.length, "model"),
    countOf(card.forecastLabels.length, "forecast"),
    countOf(card.runCount, "run"),
  ].filter(Boolean) as string[];

  // Two refs on one node: dnd-kit's, and ours for scrolling a map-selected card back into view
  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);
      registerNode(card.id, node);
    },
    [setNodeRef, registerNode, card.id]
  );

  return (
    <div
      ref={setRefs}
      // pan-y rather than none: the card drags, but a touch meant to scroll the panel still scrolls
      style={{ transform: CSS.Transform.toString(transform), transition, touchAction: "pan-y" }}
      {...attributes}
      {...listeners}
      // A short drag threshold lets the same node be both the handle and the click target
      onClick={() => onSelect(card.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(card.id);
        }
      }}
      aria-pressed={isSelected}
      className={`flex h-full w-44 shrink-0 cursor-grab flex-col gap-1.5 overflow-hidden rounded-eai-card border p-2.5 shadow-eai-card transition-shadow active:cursor-grabbing ${
        CARD_TONE[card.severity]
      } ${isDragging ? "z-10 opacity-60 shadow-eai-card-hover" : ""} ${
        // The sidebar's active tone, and the same outline the selected pin wears, so the two read as one
        isSelected ? "ring-2 ring-inset ring-eai-teal-deep" : ""
      }`}
    >
      {/* The pill takes its own line — sharing a 150px row leaves the name too narrow and it breaks mid-word */}
      {card.signal && (
        <span
          className={`eai-tag-pill-sm self-start ${
            bad ? "border-eai-danger-soft bg-eai-danger-soft text-eai-danger" : ""
          }`}
        >
          {bad ? "Failing" : "Attention"}
        </span>
      )}

      <p className="line-clamp-2 break-words font-outfit text-eai-body-sm font-semibold text-eai-ink">
        {card.name}
      </p>

      {/* Dropped once there is a reading: the pill already says the severity, and the number is what the card is for */}
      {!reading && (
        <p className="line-clamp-2 break-words font-inter text-eai-body-xs text-eai-ink-subtle">
          {card.statusLabel}
        </p>
      )}

      {/* What the asset is actually generating — the one number here that is about the asset rather than about its models */}
      {reading && (
        <div className="flex flex-col gap-0.5 border-t border-eai-border pt-1.5">
          <div className="flex items-baseline gap-1">
            <span className="font-outfit text-eai-h3 font-semibold leading-none text-eai-ink">
              {reading.value}
            </span>
            {reading.unit && (
              <span className="truncate font-inter text-eai-body-xs text-eai-ink-faint">
                {reading.unit}
              </span>
            )}
            {reading.delta && (
              <span
                className={`ml-auto shrink-0 font-outfit text-eai-body-xs font-semibold ${
                  reading.deltaDirection === "up" ? "text-eai-cta-primary" : "text-eai-danger"
                }`}
              >
                {reading.deltaDirection === "up" ? "↑" : "↓"} {reading.delta}
              </span>
            )}
          </div>
          {/* Against forecast, and how old the reading is — the number means nothing without both */}
          <p className="flex items-center justify-between gap-2 font-inter text-eai-body-xs text-eai-ink-faint">
            <span className="truncate">{reading.delta ? "vs forecast" : "Tracking forecast"}</span>
            <span className="shrink-0">{reading.age}</span>
          </p>
        </div>
      )}

      {/* Counts only without a reading — "vs forecast · 2h ago" above says more, and the rail pays for every line */}
      {!reading && counts.length > 0 && (
        <p className="font-inter text-eai-body-xs text-eai-ink-faint">{counts.join(" · ")}</p>
      )}

    </div>
  );
};

// One widget on purpose: selection runs both ways, and splitting it would grow a global state nobody can follow
const AssetMapWidget: React.FC<AssetMapWidgetProps> = ({
  cards,
  comparisons,
  isLoading,
  className,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [affectedOnly, setAffectedOnly] = useState(false);
  // Read lazily per group rather than held in one map: an order is only ever needed for the open group
  const [order, setOrder] = useState<string[]>(() => readAssetOrder(ALL_GROUPS_ORDER_KEY));
  const nodes = useRef(new Map<string, HTMLElement>());
  const rail = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => assetGroupOptions(cards), [cards]);
  const affectedCount = useMemo(() => cards.filter((card) => card.signal).length, [cards]);
  const filtered = useMemo(
    () => filterAssetCards(cards, { groupId, affectedOnly }),
    [cards, groupId, affectedOnly]
  );
  const visible = useMemo(() => orderAssetCards(filtered, order), [filtered, order]);

  // Resolved once for the visible cards rather than per tile render, since each is a walk of the asset's links
  const readings = useMemo(() => {
    if (!comparisons?.size) return new Map<string, AssetReading>();
    const now = Date.now();
    return new Map(
      visible
        .map((card) => [card.id, assetReading(card, comparisons, now)] as const)
        .filter((entry): entry is readonly [string, AssetReading] => Boolean(entry[1]))
    );
  }, [visible, comparisons]);

  // A filter that hides the selected asset leaves a pin selected that the list no longer shows
  const selected = visible.some((card) => card.id === selectedId) ? selectedId : null;

  const selectGroup = useCallback((next: string | null) => {
    setGroupId(next);
    setSelectedId(null);
    setOrder(readAssetOrder(next ?? ALL_GROUPS_ORDER_KEY));
  }, []);

  const registerNode = useCallback((id: string, node: HTMLElement | null) => {
    if (node) nodes.current.set(id, node);
    else nodes.current.delete(id);
  }, []);

  // Driving the rail directly, not scrollIntoView, which drags the whole page along when a pin brings its card into view
  useEffect(() => {
    const card = selected ? nodes.current.get(selected) : null;
    if (!card || !rail.current) return;
    const track = rail.current;
    const offset = card.getBoundingClientRect().left - track.getBoundingClientRect().left;
    const left = track.scrollLeft + offset - (track.clientWidth - card.clientWidth) / 2;
    track.scrollTo({ left, behavior: "auto" });
  }, [selected]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = visible.map((card) => card.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    // The saved order covers the whole group, not just the filtered view, or every hidden asset would be sent to the end
    const groupIds = orderAssetCards(
      filterAssetCards(cards, { groupId, affectedOnly: false }),
      order
    ).map((card) => card.id);
    const next = applyVisibleOrder(groupIds, arrayMove(ids, from, to));
    setOrder(next);
    writeAssetOrder(groupId ?? ALL_GROUPS_ORDER_KEY, next);
  };

  // Short on purpose: the picker below already names the group, and repeating it squeezes the title to an initial on a phone
  const openGroup = groups.find((group) => group.id === groupId);
  const description = openGroup
    ? `${openGroup.count} ${openGroup.count === 1 ? "asset" : "assets"}${
        openGroup.affected ? ` · ${openGroup.affected} affected` : ""
      }`
    : undefined;

  return (
    <WidgetFrame
      title="Assets"
      meta={description}
      count={affectedCount}
      isLoading={false}
      className={className}
      actionLabel="Model Garden"
      actionHref={MODEL_TEMPLATES_PATH}
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-64 w-full" radius="lg" />
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3.5 w-1/2" />
        </div>
      ) : cards.length === 0 ? (
        <EmptyRow>No assets with coordinates yet.</EmptyRow>
      ) : (
        <div className="flex h-full flex-col gap-3">
          {/* Filters wrap rather than collapse — two controls do not earn a toggle */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setAffectedOnly(false)}
              className={`eai-btn-toolbar ${!affectedOnly ? "border-eai-border-active" : ""}`}
              aria-pressed={!affectedOnly}
            >
              All {cards.length}
            </button>
            <button
              type="button"
              onClick={() => setAffectedOnly(true)}
              disabled={affectedCount === 0}
              className={`eai-btn-toolbar ${affectedOnly ? "border-eai-border-active" : ""} ${
                affectedCount === 0 ? "cursor-not-allowed opacity-50" : ""
              }`}
              aria-pressed={affectedOnly}
            >
              Affected {affectedCount}
            </button>
            {/* A button per group was a 24-button wall on real data — groups are a picker, not a pill row */}
            {groups.length > 0 && (
              <Select
                size="sm"
                aria-label="Filter assets by group"
                value={groupId ?? ""}
                onChange={(event) => selectGroup(event.target.value || null)}
                placeholder={`All groups (${groups.length})`}
                options={groups.map((group) => ({
                  value: group.id,
                  label: group.affected
                    ? `${group.name} · ${group.affected} affected`
                    : `${group.name} · ${group.count}`,
                }))}
                containerClassName="ml-auto w-full sm:w-56"
              />
            )}
          </div>

          {/* A one-card-tall rail sized to the tallest real card, so picking a group never resizes the map below it */}
          <div
            ref={rail}
            className="scrollable-div h-[10.75rem] shrink-0 overflow-x-auto overflow-y-hidden py-2.5"
          >
            {visible.length === 0 ? (
              <EmptyRow>No assets match this filter.</EmptyRow>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={visible.map((card) => card.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {/* w-max makes the trailing inset real — at the scroller's own width the right padding never reaches the extent */}
                  <div className="flex h-full w-max gap-2.5 px-2.5">
                    {visible.map((card) => (
                      <AssetTile
                        key={card.id}
                        card={card}
                        reading={readings.get(card.id) ?? null}
                        isSelected={card.id === selected}
                        onSelect={(id) => setSelectedId(id === selected ? null : id)}
                        registerNode={registerNode}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>

          <OperationsAssetMap
            cards={visible}
            selectedId={selected}
            onSelect={setSelectedId}
            className={MAP_CLASS}
          />
        </div>
      )}
    </WidgetFrame>
  );
};

export default AssetMapWidget;
